import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const toUrl = (item) => (typeof item === "string" ? item : item?.url || "").trim();

// Same file, different token/params => same key
const toKey = (url) => url.split("?")[0].split("#")[0];

export default function CoursePhotoGallery({ course }) {
  const allImages = useMemo(() => {
    const seen = new Set();
    return [course.image, ...(course.images || [])]
      .map(toUrl)
      .filter(Boolean)
      .filter((url) => {
        const key = toKey(url);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [course.image, course.images]);

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const total = allImages.length;

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prev = useCallback(
    () => setLightboxIndex((i) => (i === null ? 0 : (i - 1 + total) % total)),
    [total]
  );
  const next = useCallback(
    () => setLightboxIndex((i) => (i === null ? 0 : (i + 1) % total)),
    [total]
  );

  // Keyboard support
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, closeLightbox, prev, next]);

  if (total <= 1) return null;

  const previewImages = allImages.slice(1, 5);

  return (
    <>
      <div
        className="grid gap-2 h-48 rounded-2xl overflow-hidden"
        style={{ gridTemplateColumns: `repeat(${previewImages.length}, minmax(0, 1fr))` }}
      >
        {previewImages.map((img, i) => (
          <div
            key={img}
            className="relative cursor-pointer overflow-hidden bg-gray-200 group"
            onClick={() => setLightboxIndex(i + 1)}
          >
            <img
              src={img}
              alt={`Photo ${i + 2}`}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            />
            {i === 3 && total > 5 && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white font-bold text-lg">+{total - 5}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
          >
            <X className="h-8 w-8" />
          </button>
          <button
            type="button"
            aria-label="Previous"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 text-white hover:text-gray-300 z-10"
          >
            <ChevronLeft className="h-10 w-10" />
          </button>
          <img
            key={allImages[lightboxIndex]}
            src={allImages[lightboxIndex]}
            alt={`Photo ${lightboxIndex + 1}`}
            decoding="async"
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            aria-label="Next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 text-white hover:text-gray-300 z-10"
          >
            <ChevronRight className="h-10 w-10" />
          </button>
          <div className="absolute bottom-4 text-white text-sm">
            {lightboxIndex + 1} / {total}
          </div>
        </div>
      )}
    </>
  );
}